import { EventBus } from '@/utilities/event-bus';
import * as jwt from 'jsonwebtoken';
import { SettingsContext } from '@/utilities/settings-context';
import type { Context, RegistrationPayload } from "@noordigitalagency/accounts-ledigajobb";
import Vue from "vue";
import {initiate} from "@noordigitalagency/accounts-ledigajobb";
import moment from 'moment';

export type RoleType = 'Administrator' | 'Candidate' | 'Moderator' | 'Recruiter' | 'ContentWriter' | 'Economy' | 'InternalSales';

export function getAuthenticationToken(): string {

    return localStorage.getItem('jwt-token') || '';
}

export function getExternalAuthenticationContext() {

    return Vue.prototype.$externalAuthenticationContext as Context;
}

export function onLogIn(callback: () => void): void {

    EventBus.$on('logged-in', () => callback());
}

export function emitLoggedIn(): void {

    getExternalAuthenticationContext().cancelGoogleOneTap();

    EventBus.$emit('logged-in');
}

export function onLogOut(callback: () => void): void {

    EventBus.$on('logged-out', () => callback());
}

export function emitLoggedOut(): void {

    EventBus.$emit('logged-out');
}

export function getUserInfoFromAuthenticationToken() {
    const token = getAuthenticationToken();

    const userToken = (jwt.decode(token) as { [key: string]: string });

    return {
        email: userToken.email,

        firstName: userToken.given_name,

        lastName: userToken.family_name,

        dateOfBirth: moment(userToken.birthdate).format('YYYY-MM-DD'),

        city: userToken.city,

        country: userToken.country,

        phoneNumber: userToken.phone_number,
    }
}

export function setAuthenticationToken(token: string) {

    localStorage.setItem('jwt-token', token);

    if(SettingsContext.constants.crossDomainAuthentication) {

        getExternalAuthenticationContext().storeToken(token);
    }
}

export function isAuthenticated(): boolean {

    const token = getAuthenticationToken();

    try {

        const decoded = jwt.decode(token) as { [key: string]: unknown };

        return token.trim().length > 0 && (decoded.exp as number) * 1000 > Date.now();

    } catch {

        return false;
    }
}

export function hasRole(role: RoleType): boolean {

    if (isAuthenticated()) {

        const token = getAuthenticationToken();

        const roles = (jwt.decode(token) as { [key: string]: unknown }).role;

        return roles instanceof Array && (roles as Array<string>).some(r => r.toLowerCase() === role.toLowerCase());

    } else {

        return false;
    }
}

export function hasAnyRole(...role: RoleType[]): boolean {

    if (isAuthenticated()) {

        const token = getAuthenticationToken();

        const roles = (jwt.decode(token) as { [key: string]: unknown }).role;

        return roles instanceof Array && (roles as Array<RoleType>).some(r => role.includes(r));

    } else {

        return false;
    }
}

export function isImpersonating(): boolean {

    if (isAuthenticated()) {

        const token = getAuthenticationToken();

        const impersonator = (jwt.decode(token) as { [key: string]: unknown }).impersonator;

        return impersonator != null;

    } else {

        return false;
    }
}

export function getName(): string | null {

    if (isAuthenticated()) {

        const token = getAuthenticationToken();

        return (jwt.decode(token) as Record<string, string>).name;

    } else {

        return null;
    }
}

export function getId(): string | null {

    if (isAuthenticated()) {

        const token = getAuthenticationToken();

        return (jwt.decode(token) as Record<string, string>).nameid;

    } else {

        return null;
    }
}

export function isAdmin() {

    return hasRole('Administrator');
}

export function watchToken() {

    let loggedIn: boolean | null = null

    setInterval(() => {

        if (loggedIn == null) {

            loggedIn = isAuthenticated();

        } else {

            const authenticated = isAuthenticated();

            if (loggedIn != authenticated) {

                loggedIn = authenticated;

                if (loggedIn) {
                    emitLoggedIn();

                } else {

                    emitLoggedOut();
                }
            }
        }

    }, 1000);
}

function externalAuthentication(token: string) {

    EventBus.$emit('accounts-ledigajobb-authentication', token);
}

export function onExternalAuthentication(callback: (token: string) => void) {

    EventBus.$on('accounts-ledigajobb-authentication', (token: string) => callback(token));
}

function externalRegistration(registrationPayload: RegistrationPayload) {

    EventBus.$emit('accounts-ledigajobb-registration', registrationPayload);
}

export function onExternalRegistration(callback: (registrationPayload: RegistrationPayload) => void) {

    EventBus.$on('accounts-ledigajobb-registration', (registrationPayload: RegistrationPayload) => callback(registrationPayload));
}

function externalEmailConfirmation(email: string) {

    EventBus.$emit('accounts-ledigajobb-confirmation', email);
}

export function onExternalEmailConfirmation(callback: (email: string) => void) {

    EventBus.$on('accounts-ledigajobb-confirmation', (email: string) => callback(email));
}

export async function initiateExternalAuthentication() {

    const context = await initiate({

        googleClientId: SettingsContext.constants.googleClientId,

        accountsApiSubdomain: SettingsContext.constants.accountsApiDomainName,

        onAuthenticated: externalAuthentication,

        onShowRegistration: externalRegistration,

        onShowConfirmation: externalEmailConfirmation,

        appleClientId: SettingsContext.constants.appleClientId
    });

    Vue.prototype.$externalAuthenticationContext = context;

    if (SettingsContext.constants.crossDomainAuthentication) {

        const currentToken = getAuthenticationToken();

        const currentExpiration = currentToken && currentToken !== '' ? +((jwt.decode(currentToken) as { [key: string]: unknown })?.exp as number) : 0;

        const contextExpiration = context.token && context.token !== '' ? +((jwt.decode(context.token) as { [key: string]: unknown })?.exp as number) : 0;

        if (context.token && contextExpiration > currentExpiration) {

            setAuthenticationToken(context.token);

        } else if (currentToken && contextExpiration < currentExpiration) {

            context.storeToken(currentToken);
        }
    }
}

export async function logout() {

    setAuthenticationToken('');

    if (SettingsContext.constants.crossDomainAuthentication) {

        getExternalAuthenticationContext().removeToken();
    }

    emitLoggedOut();
}
